import React, { useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faXmark, faCheck, faMagnifyingGlass, faPaperPlane, faSpinner
} from '@fortawesome/free-solid-svg-icons';

import api from '../../utils/api';
import EmployeeAvatar from '../EmployeeAvatar';
import GroupIcon from './GroupIcon';
import { orgLabel } from '../../utils/orgTaxonomy';
import WithPresence from '../PresenceDot';
import { useRecentPicks } from '../../utils/recentPicks';
import { RecentPickLabel, RecentPickDivider } from '../RecentPickLabel';

/**
 * Pick up to five chats to forward messages into, as WhatsApp does.
 *
 * Lists only conversations this person is a member of, because those are the
 * only ones the server will let them post into. Everything else (copying the
 * content, the "Forwarded" mark, notifications) happens in
 * POST /api/conversations/forward.
 */
const MAX_TARGETS = 5;

// `commentIds` forwards task discussion messages instead of chat messages.
const ForwardModal = ({ messageIds = [], commentIds = [], onClose, onForwarded }) => {
    const count = messageIds.length + commentIds.length;
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState([]);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        let cancelled = false;
        api.get('/conversations')
            .then((res) => {
                if (!cancelled) setConversations((res.data || []).filter((c) => c.isMember !== false));
            })
            .catch(() => { if (!cancelled) setConversations([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const q = query.trim().toLowerCase();
    const visible = conversations.filter((c) => !q || (c.title || c.name || '').toLowerCase().includes(q));

    const searchRef = useRef(null);
    const recent = useRecentPicks('forward');
    // With nothing typed, the chats forwarded to lately come first.
    const { recent: recentRows, rest: otherRows } = q ? { recent: [], rest: visible } : recent.split(visible);
    const listed = [...recentRows, ...otherRows];

    const toggle = (id) => {
        if (!selected.includes(id) && selected.length < MAX_TARGETS) recent.record(id);
        // Ready for the next chat: the search empties and keeps the cursor.
        setQuery('');
        searchRef.current?.focus();
        setSelected((prev) => {
            if (prev.includes(id)) return prev.filter((x) => x !== id);
            if (prev.length >= MAX_TARGETS) {
                Swal.fire({
                    toast: true, position: 'top-end', icon: 'info',
                    title: `You can forward to up to ${MAX_TARGETS} chats`,
                    timer: 2200, showConfirmButton: false
                });
                return prev;
            }
            return [...prev, id];
        });
    };

    const send = async () => {
        if (!selected.length || sending) return;
        setSending(true);
        try {
            const res = await api.post('/conversations/forward', {
                ...(messageIds.length ? { messageIds } : {}),
                ...(commentIds.length ? { commentIds } : {}),
                conversationIds: selected
            });

            if (res.data.skippedMedia) {
                Swal.fire(
                    'Forwarded',
                    `${res.data.skippedMedia} recording${res.data.skippedMedia > 1 ? 's were' : ' was'} left out. `
                    + 'It is still being optimised and can be forwarded once that finishes.',
                    'info'
                );
            } else {
                Swal.fire({
                    toast: true, position: 'bottom', icon: 'success',
                    title: 'Forwarded', timer: 1600, showConfirmButton: false
                });
            }
            onForwarded?.(res.data.conversations || selected);
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not forward those messages.', 'error');
        } finally {
            setSending(false);
        }
    };

    const chosenNames = selected
        .map((id) => conversations.find((c) => c._id === id)?.title)
        .filter(Boolean)
        .join(', ');

    return (
        <div className="msgr-modal-back" onClick={onClose}>
            <div className="msgr-modal" onClick={(e) => e.stopPropagation()}>
                <div className="msgr-modal-head">
                    <h4>
                        Forward {count > 1 ? `${count} messages` : 'message'} to…
                    </h4>
                    <button className="msgr-icon-btn" onClick={onClose}>
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>

                <div className="msgr-modal-body">
                    <div className="msgr-search" style={{ marginBottom: 12 }}>
                        <FontAwesomeIcon icon={faMagnifyingGlass} className="msgr-search-icon" />
                        <input
                            ref={searchRef}
                            autoFocus
                            placeholder="Search chats and groups"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            style={{ border: '1px solid light-dark(#e4e6eb, var(--theme-border, #33363b))' }}
                        />
                    </div>

                    <div className="msgr-picklist">
                        {loading && <div className="msgr-empty">Loading…</div>}
                        {!loading && !visible.length && (
                            <div className="msgr-empty">No chats match that.</div>
                        )}
                        {listed.map((c, index) => {
                            const isGroup = c.kind === 'group';
                            const on = selected.includes(c._id);
                            return (
                                <React.Fragment key={c._id}>
                                {index === 0 && recentRows.length > 0 && <RecentPickLabel />}
                                {index === recentRows.length && index > 0 && <RecentPickDivider />}
                                <div
                                    key={c._id}
                                    className={`msgr-pick ${on ? 'selected' : ''}`}
                                    onClick={() => toggle(c._id)}
                                >
                                    {isGroup ? (
                                        <GroupIcon conversation={c} className="msgr-avatar" />
                                    ) : (
                                        <WithPresence userId={c.otherUser?._id} small>
                                            <EmployeeAvatar
                                                person={c.otherUser || { name: c.title, profilePic: c.avatarUrl }}
                                                className="msgr-avatar"
                                            />
                                        </WithPresence>
                                    )}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 13.5, fontWeight: 500 }}>{c.title}</div>
                                        <div style={{ fontSize: 11.5, color: 'light-dark(#667781, var(--theme-muted))' }}>
                                            {isGroup
                                                ? 'Group'
                                                : (orgLabel(c.otherUser?.jobTitle) || c.otherUser?.role || 'Direct message')}
                                        </div>
                                    </div>
                                    {on && <FontAwesomeIcon icon={faCheck} style={{ color: 'light-dark(#128c7e, var(--theme-success-ink, #7bcf9f))' }} />}
                                </div>
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>

                <div className="msgr-modal-foot">
                    <span className="msgr-forward-chosen" title={chosenNames}>
                        {chosenNames || `Choose up to ${MAX_TARGETS} chats`}
                    </span>
                    <button className="msgr-btn" onClick={onClose}>Cancel</button>
                    <button
                        className="msgr-btn primary"
                        disabled={!selected.length || sending}
                        onClick={send}
                    >
                        <FontAwesomeIcon icon={sending ? faSpinner : faPaperPlane} spin={sending} />
                        {' '}Forward
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ForwardModal;
